import React from 'react';
import { useQuery } from '@apollo/client';
import { useDeps } from 'src/hooks/useDeps';
import { BannerSkeleton } from 'src/elements/Skeletons/Skeletons';
import { differenceInSeconds } from 'date-fns';
import Bugsnag from '@bugsnag/js';
import { StaticBanner } from 'src/components/Banner/StaticBanner';
import { splitCurrencySpecific } from 'src/util/splitCurrencySpecific';

import {
    informationBarWidget as InformationBarWidgetData,
    informationBarWidget_informationBarWidget,
} from '../../queries/__generated__/informationBarWidget';
import informationBarWidget from '../../queries/informationBarWidget.graphql';

import { Banner } from './Banner';
import classes from './BannerQuery.scss';

type BannerQueryProps = Record<string, unknown>;

const getTimerSeconds = (startDateString, endDateString) => {
    const startDiff = differenceInSeconds(new Date(), new Date(startDateString));
    const endDiff = differenceInSeconds(new Date(endDateString), new Date());

    if (startDiff > 0 && endDiff > 0) return endDiff;
};

export const BannerQuery: React.FC<BannerQueryProps> = () => (
    <div className={classes.slot}>
        <BannerQueryContent />
    </div>
);

const BannerQueryContent: React.FC = () => {
    const { error, data, loading } = useQuery<InformationBarWidgetData>(informationBarWidget, {
        ssr: false,
    });

    // StaticBanner rather than nothing, so the slot is never left empty.
    if (error) return <StaticBanner />;

    if (loading) return <BannerSkeleton />;

    if (!Array.isArray(data?.informationBarWidget)) {
        Bugsnag.notify('no data for promo banner', (event) => {
            event.addMetadata('data', { data: data });
        });
        return <StaticBanner />;
    }

    // A banner for the selected currency wins over one shown for every currency.
    const { specific, generic } = splitCurrencySpecific(data?.informationBarWidget);
    const pickedBanner = pickBanner(specific) || pickBanner(generic);

    return pickedBanner ? (
        <Banner
            title={pickedBanner.title ?? ''}
            description={pickedBanner.description ?? ''}
            timerSeconds={getTimerSeconds(pickedBanner.starts_at, pickedBanner.ends_at)}
            bgColour={pickedBanner.bg_color ?? undefined}
            fontColour={pickedBanner.font_color ?? undefined}
            discountCode={pickedBanner.discount_code}
            url={pickedBanner.url}
        />
    ) : (
        <StaticBanner />
    );
};

const pickBanner = (banners: informationBarWidget_informationBarWidget[]) => {
    const enabledBanners = banners.filter((banner) => Boolean(banner.widget_parameters?.enabled));

    const enabledBannerWithMatchingTime = enabledBanners.find((banner) =>
        Boolean(getTimerSeconds(banner.widget_parameters?.starts_at, banner.widget_parameters?.ends_at)),
    )?.widget_parameters;

    const enabledBannerWithoutTime = enabledBanners.find(
        (banner) =>
            !banner.widget_parameters?.starts_at &&
            !banner.widget_parameters?.ends_at &&
            Boolean(banner.widget_parameters?.enabled),
    )?.widget_parameters;

    const discountBanner = enabledBanners.find(
        (banner) =>
            Boolean(getTimerSeconds(banner.widget_parameters?.starts_at, banner.widget_parameters?.ends_at)) &&
            banner.widget_parameters?.discount_code !== null,
    )?.widget_parameters;

    return discountBanner || enabledBannerWithMatchingTime || enabledBannerWithoutTime;
};
